import { useEffect, useState, useCallback, useMemo } from "react";
import "../Consumption.css";
import { ChartAreaLoader } from "../../../../components/Loading";
import { useSelector, useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../../../store/state/FilterLoadingState";
import NewFilterContainer from "../../../../components/NewFilterContainer ";
import FilterMenu from "../../../../components/FilterMenu";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import { useLegendFilter } from "../../../../hooks/useLegendFilter";
import { BrushStart, ChartType } from "../../../../models/chart_model";
import MainPageContainer from "../../../../components/MainPageContainer";
import SecondaryHeader from "../../../Rajasthan/Demand/SecondaryHeader";
import LegendSummaryTable, {
  ChartTable,
} from "../../../../components/table/SummaryTable";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../components/recharts/functions";
import { AddDatesWithFileName } from "../../../../helpers/helper";
import { ExchangeDataTabs } from "../../../Rajasthan/Demand/Data";
import { SearchBox } from "../../../../components/Search";
import {
  getConsumptionData,
  getGenerationData,
  getOutageData,
  getConsumptionGenerationSlotwiseData,
  getCAGSlotwiseMetrics,
  getCAGDateWiseMetrics,
} from "../../../../Rest_api/RestApi";
import Tooltip from "../../../../components/tooltip";
import { MetricChipHead } from "../../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../../icons/MinIcon.svg";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";
import { COLOR_CODES } from "../../../../common";
import { normalizeDisplayLabel } from "../../../../utility/displayText";
import { OverviewPeakAnalysis, X_AXIS_MODES } from "./OverviewPeakAnalysis";

function hashFNV1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const m = hex.replace("#", "").trim();
  if (m.length !== 6) return null;
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
    else if (max === g) h = ((b - r) / d + 2) * 60;
    else h = ((r - g) / d + 4) * 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number): string {
  const hue = ((h % 360) + 360) % 360;
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const lig = Math.max(0, Math.min(100, l)) / 100;
  const c = (1 - Math.abs(2 * lig - 1)) * sat;
  const hp = hue / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = lig - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp < 1) {
    r = c;
    g = x;
  } else if (hp < 2) {
    r = x;
    g = c;
  } else if (hp < 3) {
    g = c;
    b = x;
  } else if (hp < 4) {
    g = x;
    b = c;
  } else if (hp < 5) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Deterministic variant of a base color. Same (baseColor, seed) always → same hex.
// First occurrence (variantIndex=0) returns baseColor unchanged so branding holds.
// Golden-angle hue rotation gives even hue spread regardless of variant count.
// First occurrence (variantIndex=0) returns baseColor unchanged so branding holds.
const GOLDEN_ANGLE_DEG = 137.508;
function variantOfColor(
  baseColor: string | undefined,
  seed: string,
  variantIndex: number,
): string | undefined {
  if (!baseColor) return baseColor;
  if (variantIndex === 0) return baseColor;
  const hsl = hexToHsl(baseColor);
  if (!hsl) return baseColor;
  const hash = hashFNV1a(`${seed}|${variantIndex}`);
  // Deterministic wide hue rotation via golden angle + small jitter per seed
  const jitter = ((hash & 0xff) / 255) * 20 - 10; // ±10°
  const dH = variantIndex * GOLDEN_ANGLE_DEG + jitter;
  // Small lightness/saturation tweaks for separation, clamped for readability
  const dL = (((hash >>> 8) & 0xff) / 255) * 20 - 10;
  const dS = (((hash >>> 16) & 0xff) / 255) * 20 - 10;
  let l = hsl.l + dL;
  if (l < 32) l = 32;
  if (l > 62) l = 62;
  let s = hsl.s + dS;
  if (s < 55) s = 55;
  if (s > 95) s = 95;
  return hslToHex(hsl.h + dH, s, l);
}

const POWER_TYPE_COLORS: Record<string, string> = {
  hydro: "#008000",
  "large hydro": COLOR_CODES.largeHydro,
  solar: "#00ffff",
  wind: "#0000ff",
  thermal: "#ff681f",
  "thermal (dg)": "#8B4513",
  "thermal (gt)": "#720000",
  nuclear: "#ff0000",
  gas: COLOR_CODES.gas,
  biomass: COLOR_CODES.bioPower,
  renewable: COLOR_CODES.renewableEnergy,
};

function getPowerTypeColor(pt: string): string | undefined {
  const lower = pt.toLowerCase();
  // exact match first (handles "thermal (dg)", "thermal (gt)" etc.)
  if (POWER_TYPE_COLORS[lower]) return POWER_TYPE_COLORS[lower];
  // partial match — find the longest key contained in the power type name
  const matchedKey = Object.keys(POWER_TYPE_COLORS)
    .filter((key) => lower.includes(key))
    .sort((a, b) => b.length - a.length)[0];
  return matchedKey ? POWER_TYPE_COLORS[matchedKey] : undefined;
}

const CHART_TYPES = [
  { value: ChartType.Bar, label: "Standard Bar", stackId: undefined },
  { value: ChartType.Line, label: "Line" },
  { value: ChartType.Area, label: "Area" },
];

const Functions = [
  { value: "Cumulative", label: "Cumulative" },
  { value: "Comparative", label: "Comparative" },
];

const TOP_N_OPTIONS = [
  { label: "All", value: null as number | null },
  { label: "Top 3", value: 3 },
  { label: "Top 5", value: 5 },
  { label: "Top 10", value: 10 },
];

const GEOGRAPHY_OPTIONS = ["All India", "Region", "State"];

const REGIONS = ["Northern", "Western", "Southern", "Eastern", "North-Eastern"];

const REGION_ALIAS_MAP: Record<string, string> = {
  northern: "Northern",
  north: "Northern",
  nr: "Northern",
  nrl: "Northern",
  nrldc: "Northern",
  northernregion: "Northern",

  western: "Western",
  west: "Western",
  wr: "Western",
  wrl: "Western",
  wrldc: "Western",
  westernregion: "Western",

  southern: "Southern",
  south: "Southern",
  sr: "Southern",
  srl: "Southern",
  srldc: "Southern",
  southernregion: "Southern",

  eastern: "Eastern",
  east: "Eastern",
  er: "Eastern",
  erl: "Eastern",
  erldc: "Eastern",
  easternregion: "Eastern",

  northeast: "North-Eastern",
  northeastern: "North-Eastern",
  ne: "North-Eastern",
  ner: "North-Eastern",
  nerldc: "North-Eastern",
  northeasternregion: "North-Eastern",
};

const METRICS_OPTIONS = [
  {
    label: "Peak Demand (MW)",
    suffix: "max_demand_met_during_the_day_mw",
    displayName: "Peak Demand",
    unit: "MW",
    type: "demand",
    color: COLOR_CODES.primary,
  },
  {
    label: "Energy Met (MU)",
    suffix: "energy_met_mu",
    displayName: "Energy Met",
    unit: "MUs",
    type: "demand",
    color: COLOR_CODES.secondary,
  },
  {
    label: "Total Generation (MU)",
    suffix: "generation_mu_actual",
    displayName: "Total Generation",
    unit: "MUs",
    type: "generation",
    color: "#DF00FF",
  },
  {
    label: "Generation By Source (MU)",
    suffix: "generation_by_source",
    displayName: "Gen By Source",
    unit: "MUs",
    type: "generation",
    color: COLOR_CODES.renewableEnergy,
  },
  {
    label: "State Outage (MW)",
    suffix: "state_sector",
    displayName: "State Outage",
    unit: "MW",
    type: "outage",
    color: "#ADD8E6",
  },
  {
    label: "Central Outage (MW)",
    suffix: "central_sector",
    displayName: "Central Outage",
    unit: "MW",
    type: "outage",
    color: "#00008B",
  },
  {
    label: "Total Outage (MW)",
    suffix: "total_outage",
    displayName: "Total Outage",
    unit: "MW",
    type: "outage",
    color: "#00008B",
  },
  {
    label: "Demand Met (MW)",
    suffix: "demand_met_mw",
    displayName: "Demand Met",
    unit: "MW",
    type: "demand_slot",
    color: COLOR_CODES.secondary,
  },
  {
    label: "Total Generation (MW)",
    suffix: "total_generation_mw",
    displayName: "Total Generation",
    unit: "MW",
    type: "generation_slot",
    color: "#DF00FF",
  },
  {
    label: "Wind (MW)",
    suffix: "wind_mw",
    displayName: "Wind",
    unit: "MW",
    type: "generation_slot_source",
    color: "#0000ff",
  },
  {
    label: "Solar (MW)",
    suffix: "solar_mw",
    displayName: "Solar",
    unit: "MW",
    type: "generation_slot_source",
    color: "#00ffff",
  },
  {
    label: "Hydro (MW)",
    suffix: "hydro_mw",
    displayName: "Hydro",
    unit: "MW",
    type: "generation_slot_source",
    color: "#008000",
  },
  {
    label: "Gas (MW)",
    suffix: "gas_mw",
    displayName: "Gas",
    unit: "MW",
    type: "generation_slot_source",
    color: COLOR_CODES.gas,
  },
  {
    label: "Thermal (MW)",
    suffix: "thermal_mw",
    displayName: "Thermal",
    unit: "MW",
    type: "generation_slot_source",
    color: "#ff681f",
  },
  {
    label: "Others (MW)",
    suffix: "others_mw",
    displayName: "Others",
    unit: "MW",
    type: "generation_slot_source",
    color: "#9B59B6",
  },
  {
    label: "Frequency (Hz)",
    suffix: "frequency_hz",
    displayName: "Frequency",
    unit: "Hz",
    type: "frequency_slot",
    color: "#E74C3C",
  },
];

export const formatDateToDDMMYYYY = (date: Date | string): string => {
  const dateObj = new Date(date);
  const day = dateObj.getDate().toString().padStart(2, "0");
  const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
  const year = dateObj.getFullYear();
  return `${day}-${month}-${year}`;
};

export function getWeekLabel(dateString: string): string {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const date = new Date(dateString);
  const weekNum = Math.ceil(date.getDate() / 7);
  return `Week ${weekNum} (${monthNames[date.getMonth()]} ${date.getFullYear()})`;
}

export function getMonthWithYear(dateString: string): string {
  const date = new Date(dateString);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  return `${month}-${year}`;
}

const STATE_REGION_MAP: Record<string, string> = {
  Delhi: "Northern",
  Haryana: "Northern",
  "Himachal Pradesh": "Northern",
  "Jammu & Kashmir": "Northern",
  "Jammu And Kashmir": "Northern",
  Punjab: "Northern",
  Rajasthan: "Northern",
  "Uttar Pradesh": "Northern",
  Uttarakhand: "Northern",
  Chandigarh: "Northern",
  Chhattisgarh: "Western",
  Goa: "Western",
  Gujarat: "Western",
  "Madhya Pradesh": "Western",
  Maharashtra: "Western",
  "Andhra Pradesh": "Southern",
  Karnataka: "Southern",
  Kerala: "Southern",
  "Tamil Nadu": "Southern",
  Telangana: "Southern",
  Puducherry: "Southern",
  Bihar: "Eastern",
  Jharkhand: "Eastern",
  Odisha: "Eastern",
  "West Bengal": "Eastern",
  Sikkim: "Eastern",
  "Arunachal Pradesh": "North-Eastern",
  Assam: "North-Eastern",
  Manipur: "North-Eastern",
  Meghalaya: "North-Eastern",
  Mizoram: "North-Eastern",
  Nagaland: "North-Eastern",
  Tripura: "North-Eastern",
};

const titleCase = (str: string) => {
  if (!str) return "Unknown";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.substring(1))
    .join(" ");
};

const normalizeRegionName = (region?: string | null) => {
  if (!region) return "Unknown";

  const normalizedKey = region
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  const aliasMatch = REGION_ALIAS_MAP[normalizedKey];
  if (aliasMatch) return aliasMatch;

  const fallback = titleCase(region);
  return fallback === "North Eastern" ? "North-Eastern" : fallback;
};

let Intervals = [
  {
    label: "Time Slot",
    key: "time_block",
    values: ["time_block"],
    isTime: false,
  },
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Week", key: "week", values: ["week"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

export function Overview() {
  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);

  function handleXAxisModeChange(_item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  const [chartData, setChartData] = useState<any[]>([]);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const dispatch = useDispatch();

  // Clear the FilterMenu loading spinner whenever the X-Axis mode changes.
  // Without this, the spinner set by FilterMenu on click never gets cleared
  // because no other effect in this component watches selectedXAxisMode.
  useEffect(() => {
    dispatch(setLoadingFilterName(""));
  }, [selectedXAxisMode, dispatch]);

  const [dataLoading, setDataLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const dates = DefaultDates[activeTab].defaultRangeDate;

  const [selectedInterval, setSelectedInterval] = useState(Intervals[1]);
  const [selectedFunction, setSelectedFunction] = useState(Functions[1]);

  const [selectedGeography, setSelectedGeography] = useState(
    GEOGRAPHY_OPTIONS[0],
  );
  const [selectedTopN, setSelectedTopN] = useState<number | null>(null);

  const [selectedChartType, setSelectedChartType] = useState(
    CHART_TYPES.find((t) => t.label === "Line") || CHART_TYPES[0],
  );

  const [selectedState, setSelectedState] = useState<
    { value: string; label: string }[]
  >([]);
  const [states, setStates] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);

  const [selectedCategories, setSelectedCategories] = useState<string[]>([
    "Peak Demand",
    "Demand",
    "Generation",
    "Frequency",
  ]);

  const [slotData, setSlotData] = useState<any[]>([]);
  const [slotDataLoading, setSlotDataLoading] = useState(false);

  const selectedMetrics = useMemo(() => {
    const isSlot = selectedInterval.key === "time_block";
    const metrics: (typeof METRICS_OPTIONS)[number][] = [];
    const activeCategories = isSlot
      ? selectedCategories.filter(
          (c) => c === "Demand" || c === "Generation" || c === "Frequency",
        )
      : selectedCategories;

    if (activeCategories.includes("Peak Demand") && !isSlot) {
      metrics.push(METRICS_OPTIONS[0]);
    }

    if (activeCategories.includes("Demand")) {
      if (isSlot) {
        const slotDemand = METRICS_OPTIONS.find(
          (m) => m.suffix === "demand_met_mw",
        );
        if (slotDemand) metrics.push(slotDemand);
      } else {
        metrics.push(METRICS_OPTIONS[1]);
      }
    }

    if (activeCategories.includes("Generation")) {
      if (isSlot) {
        const isCumulative = selectedFunction.value === "Cumulative";
        if (isCumulative) {
          const totalGen = METRICS_OPTIONS.find(
            (m) => m.suffix === "total_generation_mw",
          );
          if (totalGen) metrics.push(totalGen);
        } else {
          METRICS_OPTIONS.filter(
            (m) =>
              m.type === "generation_slot" ||
              m.type === "generation_slot_source",
          ).forEach((m) => metrics.push(m));
        }
      } else {
        METRICS_OPTIONS.filter((m) => m.type === "generation").forEach((m) =>
          metrics.push(m),
        );
      }
    }

    if (activeCategories.includes("Frequency") && isSlot) {
      const freqMetric = METRICS_OPTIONS.find(
        (m) => m.suffix === "frequency_hz",
      );
      if (freqMetric) metrics.push(freqMetric);
    }

    if (activeCategories.includes("Outage") && !isSlot) {
      METRICS_OPTIONS.filter((m) => m.type === "outage").forEach((m) =>
        metrics.push(m),
      );
    }
    return metrics;
  }, [selectedCategories, selectedInterval, selectedFunction.value]);

  const [powerTypes, setPowerTypes] = useState<string[]>([]);
  const [selectedPowerType, setSelectedPowerType] = useState<string[]>([]);

  const [demandData, setDemandData] = useState<any[]>([]);
  const [generationData, setGenerationData] = useState<any[]>([]);
  const [outageData, setOutageData] = useState<any[]>([]);

  const [TableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);

  const [metricsData, setMetricsData] = useState<any>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);

  function formatDateToYYYYMMDD(date: string) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const fetchMetrics = useCallback(async () => {
    if (dataLoading) return;
    setMetricsLoading(true);
    try {
      const startDateStr = formatDateToYYYYMMDD(dates.startDate);
      const endDateStr = formatDateToYYYYMMDD(dates.endDate);
      const selectedStatesList =
        selectedGeography === "All India"
          ? []
          : selectedState.map((s) => s.value);

      if (selectedInterval.key === "time_block") {
        const blocksParam =
          selectedBlock.length > 0
            ? selectedBlock.map((item) => item.value)
            : undefined;
        const res = await getCAGSlotwiseMetrics(
          [{ startDate: startDateStr, endDate: endDateStr }],
          selectedStatesList,
          blocksParam,
        );
        setMetricsData(res);
      } else {
        const res = await getCAGDateWiseMetrics(
          [{ startDate: startDateStr, endDate: endDateStr }],
          selectedStatesList,
        );
        setMetricsData(res);
      }
    } catch (err) {
      console.error("Failed to fetch consumption generation summary:", err);
    } finally {
      setMetricsLoading(false);
    }
  }, [
    dates.startDate,
    dates.endDate,
    selectedState,
    dataLoading,
    selectedGeography,
    selectedInterval.key,
    selectedBlock,
  ]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const fetchAllData = useCallback(async () => {
    setDataLoading(true);
    try {
      const startDateStr = formatDateToYYYYMMDD(dates.startDate);
      const endDateStr = formatDateToYYYYMMDD(dates.endDate);

      const [demandRes, genRes, outageRes] = await Promise.all([
        getConsumptionData(startDateStr, endDateStr),
        getGenerationData(startDateStr, endDateStr),
        getOutageData(startDateStr, endDateStr),
      ]);

      const dData = demandRes as any[];
      const gData = genRes as any[];
      const oData = outageRes as any[];

      const allStatesSet = new Set<string>();
      dData.forEach((d) => allStatesSet.add(titleCase(d.state)));
      gData.forEach((g) =>
        allStatesSet.add(titleCase(g.state__name || g.state || "Unknown")),
      );

      const uniqueStates = Array.from(allStatesSet).sort((a, b) =>
        a.localeCompare(b),
      );
      setStates(uniqueStates);

      const uniquePowerTypes = Array.from(
        new Set(
          gData.map((g: any) =>
            g.power_type__name
              ? g.power_type__name.charAt(0).toUpperCase() +
                g.power_type__name.slice(1).toLowerCase()
              : "Unknown",
          ),
        ),
      )
        .filter((pt) => pt !== "Unknown" && pt !== "0")
        .sort() as string[];
      setPowerTypes(uniquePowerTypes);

      // Default to empty so Amnsil doesn't get pre-selected
      // setSelectedState((prev) => {
      //   if (prev.length === 0 && uniqueStates.length > 0) {
      //     return [{ value: uniqueStates[0], label: uniqueStates[0] }];
      //   }
      //   return prev;
      // });

      setDemandData(
        dData.map((item) => ({
          ...item,
          state: titleCase(item.state),
        })),
      );
      setGenerationData(
        gData.map((item) => ({
          ...item,
          state: titleCase(item.state__name || item.state || "Unknown"),
          power_type: item.power_type__name
            ? item.power_type__name.charAt(0).toUpperCase() +
              item.power_type__name.slice(1).toLowerCase()
            : "Unknown",
        })),
      );
      setOutageData(
        oData.map((item) => ({
          ...item,
          region: normalizeRegionName(item.region__name || item.region),
        })),
      );
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setDataLoading(false);
    }
  }, [dates.startDate, dates.endDate]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const fetchSlotData = useCallback(async () => {
    if (selectedInterval.key !== "time_block") return;
    setSlotDataLoading(true);
    try {
      const startDateStr = formatDateToYYYYMMDD(dates.startDate);
      const endDateStr = formatDateToYYYYMMDD(dates.endDate);
      const res = await getConsumptionGenerationSlotwiseData(
        startDateStr,
        endDateStr,
      );
      setSlotData(res as any[]);
    } catch (err) {
      console.error("Failed to fetch slotwise data:", err);
    } finally {
      setSlotDataLoading(false);
    }
  }, [dates.startDate, dates.endDate, selectedInterval.key]);

  useEffect(() => {
    fetchSlotData();
  }, [fetchSlotData]);

  const slotStatesOptions = useMemo(() => {
    return Array.from(new Set(slotData.map((d) => d.state))).sort();
  }, [slotData]);

  useEffect(() => {
    setSelectedState((prev) => {
      const allowed =
        selectedInterval.key === "time_block" ? slotStatesOptions : states;
      if (allowed.length === 0) return prev;
      const valid = prev.filter((s) => allowed.includes(s.value));
      return valid.length === prev.length ? prev : valid;
    });
  }, [selectedInterval.key, slotStatesOptions, states]);

  useEffect(() => {
    if (selectedRegions.length === 0) return;
    const regionStates = states.filter((s) =>
      selectedRegions.includes(STATE_REGION_MAP[s] ?? ""),
    );
    setSelectedState((prev) => {
      const prevValues = prev.map((s) => s.value);
      const added = regionStates
        .filter((s) => !prevValues.includes(s))
        .map((s) => ({ value: s, label: s }));
      return [...prev, ...added];
    });
  }, [selectedRegions, states]);

  useEffect(() => {
    setFilterLoading(true);
    const timeoutId = setTimeout(() => {
      let mergedList: any[] = [];
      const isTimeSlot = selectedInterval.key === "time_block";

      if (isTimeSlot) {
        // ── Time Slot branch (national level only) ────────────────────────────
        let filteredSlot = slotData;

        if (selectedBlock.length > 0) {
          const blockValues = selectedBlock.map((b) => b.value);
          filteredSlot = filteredSlot.filter((d) =>
            blockValues.includes(d.time_block),
          );
        }

        mergedList = [...filteredSlot].sort((a, b) => {
          if (a.date !== b.date) return a.date.localeCompare(b.date);
          return a.time_block - b.time_block;
        });

        const SLOT_NATIONAL_KEYS = [
          "demand_met_mw",
          "total_generation_mw",
          "wind_mw",
          "solar_mw",
          "hydro_mw",
          "gas_mw",
          "thermal_mw",
          "others_mw",
          "frequency_hz",
        ] as const;

        const slotAggKeys: string[] = [];
        const slotAvgKeys: string[] = [];
        SLOT_NATIONAL_KEYS.forEach((k) => {
          if (selectedMetrics.some((m) => m.suffix === k)) {
            slotAggKeys.push(k);
            if (k === "frequency_hz") slotAvgKeys.push(k);
          }
        });

        const slotPar: ChartDataParams = {
          keys: ["date", "time_block"],
          tableKeys: ["date", "time_block"],
          groupBy: [],
          volumeKey: "",
          aggregationKeys: slotAggKeys,
          averageKeys: slotAvgKeys,
          averageDuplicationKeys: ["date", "time_block"],
          priceKey: "",
          data: mergedList,
        };
        const slotCData = FormatChartData(slotPar);
        setChartData(slotCData.data);
        setChartKeys(slotCData.keys);
        setTableData(slotCData.tableData || []);
        setFilterLoading(false);
        dispatch(setLoadingFilterName(""));
        return;
      }

      // ── Regular date/week/month branch ───────────────────────────────────────
      type GroupedRecord = Record<string, any>;
      const groupedData: Record<string, GroupedRecord> = {};

      const getKey = (dateStr: string, geoStr: string) => {
        if (selectedGeography === "All India") return dateStr;
        return `${dateStr}__${geoStr}`;
      };

      const initOrGetGroup = (dateStr: string, geoStr: string) => {
        const key = getKey(dateStr, geoStr);
        if (!groupedData[key]) {
          groupedData[key] = {
            date: dateStr,
            geo_key: selectedGeography === "All India" ? "All India" : geoStr,
            month: getMonthWithYear(dateStr),
            week: getWeekLabel(dateStr),
            energy_met_mu: 0,
            max_demand_met_during_the_day_mw: 0,
            generation_mu_actual: 0,
            central_sector: 0,
            state_sector: 0,
            total_outage: 0,
          };
        }
        return groupedData[key];
      };

      // 1. Process Demand
      let filteredDemand = demandData;
      if (selectedGeography === "State" && selectedState.length > 0) {
        filteredDemand = demandData.filter((d) =>
          selectedState.some(
            (s) =>
              s.value.trim().toLowerCase() === titleCase(d.state).toLowerCase(),
          ),
        );
      } else if (selectedGeography === "Region" && selectedRegions.length > 0) {
        filteredDemand = demandData.filter((d) =>
          selectedRegions.includes(
            STATE_REGION_MAP[titleCase(d.state)] || "Unknown",
          ),
        );
      }

      filteredDemand.forEach((d) => {
        const geoStr =
          selectedGeography === "Region"
            ? STATE_REGION_MAP[titleCase(d.state)] || "Unknown"
            : titleCase(d.state);
        if (selectedGeography === "Region" && geoStr === "Unknown") return;
        const group = initOrGetGroup(formatDateToYYYYMMDD(d.date), geoStr);
        group.energy_met_mu += Number(d.energy_met_mu || 0);
        group.max_demand_met_during_the_day_mw += Number(
          d.max_demand_met_during_the_day_mw || 0,
        );
      });

      // 2. Process Generation
      let filteredGen = generationData;
      if (selectedGeography === "State" && selectedState.length > 0) {
        filteredGen = generationData.filter((g) =>
          selectedState.some(
            (s) =>
              s.value.trim().toLowerCase() === titleCase(g.state).toLowerCase(),
          ),
        );
      } else if (selectedGeography === "Region" && selectedRegions.length > 0) {
        filteredGen = generationData.filter((g) =>
          selectedRegions.includes(
            STATE_REGION_MAP[titleCase(g.state)] || "Unknown",
          ),
        );
      }

      if (selectedPowerType.length > 0) {
        filteredGen = filteredGen.filter((g) =>
          selectedPowerType.includes(g.power_type),
        );
      }

      filteredGen.forEach((g) => {
        const stateName = titleCase(g.state);
        const geoStr =
          selectedGeography === "Region"
            ? STATE_REGION_MAP[stateName] || "Unknown"
            : stateName;
        if (selectedGeography === "Region" && geoStr === "Unknown") return;
        const group = initOrGetGroup(formatDateToYYYYMMDD(g.date), geoStr);
        group.generation_mu_actual += Number(g.generation_mu_actual || 0);

        const ptKey = `gen_source_${g.power_type}`;
        group[ptKey] =
          (group[ptKey] || 0) + Number(g.generation_mu_actual || 0);
      });

      // 3. Process Outage
      const activeStatesByRegion: Record<string, string[]> = {};
      if (selectedGeography === "State") {
        selectedState.forEach((s) => {
          const region = STATE_REGION_MAP[s.value];
          if (region) {
            activeStatesByRegion[region] = activeStatesByRegion[region] || [];
            activeStatesByRegion[region].push(s.value);
          }
        });
      }

      outageData.forEach((o) => {
        const normalizedDate = formatDateToYYYYMMDD(o.date);
        const regionName = normalizeRegionName(o.region);
        if (regionName === "Unknown") return;

        if (selectedGeography === "All India") {
          const group = initOrGetGroup(normalizedDate, "All India");
          const central = Number(o.central_sector || 0);
          const state = Number(o.state_sector || 0);
          group.central_sector += central;
          group.state_sector += state;
          group.total_outage += central + state;
        } else if (selectedGeography === "Region") {
          if (
            selectedRegions.length === 0 ||
            selectedRegions.includes(regionName)
          ) {
            const group = initOrGetGroup(normalizedDate, regionName);
            group.central_sector += Number(o.central_sector || 0);
            group.state_sector += Number(o.state_sector || 0);
          }
        } else if (selectedGeography === "State") {
          const statesInRegion = activeStatesByRegion[regionName] || [];
          if (statesInRegion.length > 0) {
            const centralShare =
              Number(o.central_sector || 0) / statesInRegion.length;
            const stateShare =
              Number(o.state_sector || 0) / statesInRegion.length;
            statesInRegion.forEach((st) => {
              const group = initOrGetGroup(normalizedDate, st);
              group.central_sector += centralShare;
              group.state_sector += stateShare;
            });
          }
        }
      });

      mergedList = Object.values(groupedData).sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        if (dateA !== dateB) return dateA - dateB;
        return (a.geo_key || "").localeCompare(b.geo_key || "");
      });
      if (selectedGeography === "State" && selectedTopN !== null) {
        const stateTotals: Record<string, number> = {};
        mergedList.forEach((item) => {
          stateTotals[item.geo_key] =
            (stateTotals[item.geo_key] ?? 0) +
            item.energy_met_mu +
            item.generation_mu_actual;
        });
        const topStates = Object.entries(stateTotals)
          .sort((a, b) => b[1] - a[1])
          .slice(0, selectedTopN)
          .map(([s]) => s);
        mergedList = mergedList.filter((item) =>
          topStates.includes(item.geo_key),
        );
      }

      const activeAggregationKeys: string[] = [];
      const activeAverageKeys: string[] = [];

      if (
        selectedMetrics.some(
          (m) => m.suffix === "max_demand_met_during_the_day_mw",
        )
      ) {
        activeAggregationKeys.push("max_demand_met_during_the_day_mw");
        activeAverageKeys.push("max_demand_met_during_the_day_mw");
      }
      if (selectedMetrics.some((m) => m.suffix === "energy_met_mu")) {
        activeAggregationKeys.push("energy_met_mu");
      }
      if (selectedMetrics.some((m) => m.suffix === "generation_mu_actual")) {
        activeAggregationKeys.push("generation_mu_actual");
      }
      if (selectedMetrics.some((m) => m.suffix === "generation_by_source")) {
        const dynamicPtKeys = Array.from(
          new Set(
            mergedList.flatMap((obj) =>
              Object.keys(obj).filter((k) => k.startsWith("gen_source_")),
            ),
          ),
        );
        activeAggregationKeys.push(...dynamicPtKeys);
      }
      const outageIsTotal =
        selectedFunction.value === "Cumulative" &&
        selectedGeography === "All India";
      if (
        selectedMetrics.some(
          (m) => m.suffix === "state_sector" || m.suffix === "central_sector",
        )
      ) {
        if (outageIsTotal) {
          activeAggregationKeys.push("total_outage");
          activeAverageKeys.push("total_outage");
        } else {
          if (selectedMetrics.some((m) => m.suffix === "state_sector")) {
            activeAggregationKeys.push("state_sector");
            activeAverageKeys.push("state_sector");
          }
          if (selectedMetrics.some((m) => m.suffix === "central_sector")) {
            activeAggregationKeys.push("central_sector");
            activeAverageKeys.push("central_sector");
          }
        }
      }

      const par: ChartDataParams = {
        keys: [selectedInterval.values[0]],
        tableKeys: ["date", "week", "month"],
        groupBy:
          selectedFunction.value === "Comparative" &&
          selectedGeography !== "All India"
            ? ["geo_key"]
            : [],
        volumeKey: ``,
        aggregationKeys: activeAggregationKeys,
        averageKeys: activeAverageKeys,
        averageDuplicationKeys: ["date"],
        priceKey: "",
        data: mergedList,
      };

      const cData = FormatChartData(par);
      setChartData(cData.data);
      setChartKeys(cData.keys);
      setTableData(cData.tableData || []);
      setFilterLoading(false);
      dispatch(setLoadingFilterName(""));
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [
    demandData,
    generationData,
    outageData,
    selectedFunction.value,
    selectedInterval,
    selectedState,
    selectedGeography,
    selectedRegions,
    selectedTopN,
    selectedMetrics,
    selectedPowerType,
    states,
    slotData,
    selectedBlock,
    dispatch,
  ]);

  const legends = useMemo(() => {
    const finalLegends: any[] = [];
    const isComparativeGeo =
      selectedFunction.value === "Comparative" &&
      selectedGeography !== "All India";
    const hasMultiSeries = isComparativeGeo && chartKeys.length > 1;

    // Deterministic variant index per geo key (stable across refresh).
    // Sorted lexicographically so first key keeps base color consistently.
    const sortedKeys = [...chartKeys].sort((a, b) =>
      String(a).localeCompare(String(b)),
    );
    const variantIndexByKey: Record<string, number> = {};
    sortedKeys.forEach((k, i) => {
      variantIndexByKey[k] = i;
    });

    chartKeys.forEach((key) => {
      const keyPrefix = `${key}_`;
      const displayNamePrefix =
        isComparativeGeo && key && key !== "undefined" && key !== ""
          ? `${normalizeDisplayLabel(key)} - `
          : "";
      const vIdx = hasMultiSeries ? (variantIndexByKey[key] ?? 0) : 0;

      selectedMetrics.forEach((metric) => {
        if (metric.suffix === "generation_by_source") {
          const genIsTotalOnly =
            selectedFunction.value === "Cumulative" &&
            selectedGeography === "All India";
          if (genIsTotalOnly) return;
          const powerTypesToMap =
            selectedPowerType.length > 0 ? selectedPowerType : powerTypes;
          powerTypesToMap.forEach((pt) => {
            const dataKey = `${keyPrefix}gen_source_${pt}`;
            const baseColor = getPowerTypeColor(pt);
            finalLegends.push({
              dataKey,
              name: `${displayNamePrefix}${pt} (MUs)`.trim(),
              unit: "MUs",
              yAxisId: "right",
              type: ChartType.Area,
              stackId: "gen_source_stack",
              legendColor: variantOfColor(
                baseColor,
                `${pt}|${metric.suffix}`,
                vIdx,
              ),
            });
          });
        } else if (metric.type === "frequency_slot") {
          finalLegends.push({
            dataKey: `${keyPrefix}${metric.suffix}`,
            name: `${displayNamePrefix}${metric.displayName} (${metric.unit})`.trim(),
            unit: metric.unit,
            yAxisId: "right",
            type: ChartType.Line,
            stackId: undefined,
            legendColor: variantOfColor(metric.color, metric.suffix, vIdx),
          });
        } else if (metric.type === "generation_slot_source") {
          finalLegends.push({
            dataKey: `${keyPrefix}${metric.suffix}`,
            name: `${displayNamePrefix}${metric.displayName} (${metric.unit})`.trim(),
            unit: metric.unit,
            yAxisId: "left",
            type: ChartType.Area,
            stackId: "slot_source_stack",
            legendColor: variantOfColor(metric.color, metric.suffix, vIdx),
          });
        } else if (
          metric.type === "generation" ||
          metric.type === "generation_slot"
        ) {
          const genIsTotalOnly =
            selectedFunction.value === "Cumulative" &&
            selectedGeography === "All India";
          if (!genIsTotalOnly && metric.suffix === "generation_mu_actual")
            return;
          if (
            selectedFunction.value === "Comparative" &&
            metric.suffix === "total_generation_mw"
          )
            return;
          finalLegends.push({
            dataKey: `${keyPrefix}${metric.suffix}`,
            name: `${displayNamePrefix}${metric.displayName} (${metric.unit})`.trim(),
            unit: metric.unit,
            yAxisId: metric.unit === "MW" ? "left" : "right",
            type: ChartType.Area,
            stackId: selectedChartType.stackId,
            legendColor: variantOfColor(metric.color, metric.suffix, vIdx),
          });
        } else if (metric.type === "outage") {
          const outageIsTotal =
            selectedFunction.value === "Cumulative" &&
            selectedGeography === "All India";
          if (outageIsTotal) {
            if (metric.suffix !== "total_outage") return;
          } else {
            if (metric.suffix === "total_outage") return;
          }
          finalLegends.push({
            dataKey: `${keyPrefix}${metric.suffix}`,
            name: `${displayNamePrefix}${metric.displayName} (${metric.unit})`.trim(),
            unit: metric.unit,
            yAxisId: "left",
            type: ChartType.Bar,
            stackId: undefined,
            legendColor: variantOfColor(metric.color, metric.suffix, vIdx),
          });
        } else {
          finalLegends.push({
            dataKey: `${keyPrefix}${metric.suffix}`,
            name: `${displayNamePrefix}${metric.displayName} (${metric.unit})`.trim(),
            unit: metric.unit,
            yAxisId: metric.unit === "MW" ? "left" : "right",
            type: selectedChartType.value,
            stackId: selectedChartType.stackId,
            legendColor: variantOfColor(metric.color, metric.suffix, vIdx),
          });
        }
      });
    });

    return finalLegends.sort((a, b) =>
      a.name.trim().localeCompare(b.name.trim()),
    );
  }, [
    selectedMetrics,
    chartKeys,
    selectedChartType,
    selectedPowerType,
    powerTypes,
    selectedGeography,
    selectedFunction.value,
  ]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const hasRightAxis = useMemo(
    () => legends.some((l: any) => l.yAxisId === "right"),
    [legends],
  );

  const rightAxisDomain = useMemo<[number, number] | undefined>(() => {
    if (!hasRightAxis || !chartData.length) return undefined;
    const rightLegends = legends.filter((l: any) => l.yAxisId === "right");
    const allFrequency = rightLegends.every((l: any) =>
      l.dataKey.includes("frequency_hz"),
    );
    if (allFrequency) return [49, 51];
    const rightKeys = rightLegends
      .filter((l: any) => !l.dataKey.includes("frequency_hz"))
      .map((l: any) => l.dataKey);
    let max = 0;
    chartData.forEach((row: any) => {
      rightKeys.forEach((k: string) => {
        const v = Number(row[k] ?? 0);
        if (v > max) max = v;
      });
    });
    if (max <= 0) return undefined;
    return [0, Math.ceil(max * 1.1)];
  }, [legends, chartData, hasRightAxis]);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    extraKeys:
      selectedInterval.key === "time_block"
        ? ["date", "time_block"]
        : [selectedInterval.values[0]],
    extraLabels:
      selectedInterval.key === "time_block"
        ? ["Date", "Time Slot"]
        : [selectedInterval.label],
    defaultSortKey: selectedInterval.key === "time_block" ? "date" : undefined,
    filename: AddDatesWithFileName({
      fileName: "Consumption & Generation Overview",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
  };

  const isTimeSlot = selectedInterval.key === "time_block";
  const isHeatMap = selectedChartType.label === "Heat Map";

  // Time Slot Values
  const maxLoad = metricsData?.load_metrics?.["Max Load (MW)"]?.value ?? null;
  const minLoad = metricsData?.load_metrics?.["Min Load (MW)"]?.value ?? null;
  const maxGen =
    metricsData?.generation_metrics?.["Max Generation (MW)"]?.value ?? null;
  const minGen =
    metricsData?.generation_metrics?.["Min Generation (MW)"]?.value ?? null;
  const maxFrency =
    metricsData?.frequency_metrics?.["Max Frequency (Hz)"]?.value ?? null;
  const avgFrency =
    metricsData?.frequency_metrics?.["Avg Frequency (Hz)"]?.value ?? null;

  const maxSurplus =
    metricsData?.surplus_deficit_metrics?.["Max Surplus/Deficit (MW)"]?.value ??
    null;
  const minSurplus =
    metricsData?.surplus_deficit_metrics?.["Min Surplus/Deficit (MW)"]?.value ??
    null;

  // Date/Week/Month Values
  const peakDemand =
    metricsData?.demand_metrics?.["Peak Demand (MW)"]?.value ?? null;
  const avgDemand =
    metricsData?.demand_metrics?.["Average Demand (MW)"]?.value ?? null;
  const peakGen =
    metricsData?.generation_metrics?.["Peak Generation (MU)"]?.value ?? null;
  const avgGen =
    metricsData?.generation_metrics?.["Average Generation (MU)"]?.value ?? null;
  const peakOutage =
    metricsData?.outage_metrics?.["Peak Outage (MW)"]?.value ?? null;
  const avgOutage =
    metricsData?.outage_metrics?.["Average Outage (MW)"]?.value ?? null;

  // Single return keeps OverviewPeakAnalysis in the same tree position so React
  // reuses the same component instance on every mode switch — state is preserved.
  return (
    <>
      {/* Always mounted; hidden via CSS when not in peak mode so state persists */}
      <div style={{ display: selectedXAxisMode.value === "peak" ? undefined : "none" }}>
        <OverviewPeakAnalysis onXAxisModeChange={handleXAxisModeChange} />
      </div>

      {selectedXAxisMode.value !== "peak" && (
      <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((m) => m.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />
          <FilterMenu
            title="Interval"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              const newInterval = Intervals[index];
              setSelectedInterval(newInterval);
              if (newInterval.key === "time_block") {
                const filtered = selectedCategories.filter(
                  (c) =>
                    c === "Demand" || c === "Generation" || c === "Frequency",
                );
                if (filtered.length === 0) {
                  setSelectedCategories(["Demand", "Generation"]);
                  if (selectedChartType.label === "Standard Bar") {
                    setSelectedChartType(CHART_TYPES[1]);
                  }
                } else {
                  setSelectedCategories(filtered);
                }
                setSelectedGeography("All India");
                setSelectedState([]);
                setSelectedRegions([]);
              }
            }}
          />
          <FilterMenu
            title="Function"
            items={Functions.map((item) => item.label)}
            selectedItems={[selectedFunction.label]}
            onSelect={(item, index) => setSelectedFunction(Functions[index])}
            disabled={isHeatMap}
          />
          <FilterMenu
            title="Aggregation"
            items={GEOGRAPHY_OPTIONS}
            selectedItems={[selectedGeography]}
            onSelect={(item, index) => {
              if (
                selectedCategories.includes("Outage") &&
                GEOGRAPHY_OPTIONS[index] === "State"
              )
                return;
              setSelectedGeography(GEOGRAPHY_OPTIONS[index]);
              if (GEOGRAPHY_OPTIONS[index] === "All India") {
                setSelectedState([]);
                setSelectedRegions([]);
              }
            }}
            disabled={isTimeSlot}
            disabledItems={
              selectedCategories.includes("Outage") ? ["State"] : []
            }
            disabledTooltip={
              isTimeSlot
                ? "State/Region data not available at time slot level"
                : "State aggregation is not available when Outage is selected"
            }
          />
          <FilterMenu
            title="Top N States"
            items={TOP_N_OPTIONS.map((o) => o.label)}
            selectedItems={[
              TOP_N_OPTIONS.find((o) => o.value === selectedTopN)?.label ??
                "All",
            ]}
            onSelect={(item, index) =>
              setSelectedTopN(TOP_N_OPTIONS[index].value)
            }
            disabled={selectedGeography !== "State" || isTimeSlot}
          />
          <FilterMenu
            title="Variable"
            items={
              selectedInterval.key === "time_block"
                ? ["Demand", "Generation", "Frequency"]
                : ["Peak Demand", "Demand", "Generation", "Outage"]
            }
            selectedItems={selectedCategories}
            setSelectedItems={(cats) => {
              setSelectedCategories(cats);
              if (cats.includes("Outage") && selectedGeography === "State") {
                setSelectedGeography("All India");
                setSelectedState([]);
              }
              if (cats.includes("Outage")) {
                setSelectedChartType(CHART_TYPES[0]);
              } else if (cats.includes("Generation")) {
                setSelectedChartType((prev) =>
                  prev.label === "Standard Bar" ? CHART_TYPES[1] : prev,
                );
              }
            }}
            disabled={isHeatMap}
          />
          <FilterMenu
            title="Chart Type"
            items={CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              const next = CHART_TYPES[index];
              setSelectedChartType(next);
            }}
            disabledItems={(() => {
              const hasGeneration = selectedCategories.some(
                (c) => c === "Generation",
              );
              const disabled: string[] = [];
              if (hasGeneration) disabled.push("Standard Bar");
              return disabled;
            })()}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
        <div className="flex flex-wrap gap-4 my-0 justify-center">
          <MetricChipHead
            icon={<PeakIcon width={20} height={19} />}
            title="Demand"
            items={
              isTimeSlot
                ? [
                    {
                      label: "Max",
                      value: metricsLoading ? null : maxLoad,
                      unit: "MW",
                    },
                    {
                      label: "Min",
                      value: metricsLoading ? null : minLoad,
                      unit: "MW",
                    },
                  ]
                : [
                    {
                      label: "Peak",
                      value: metricsLoading ? null : peakDemand,
                      unit: "MW",
                    },
                    {
                      label: "Average",
                      value: metricsLoading ? null : avgDemand,
                      unit: "MW",
                    },
                  ]
            }
          />
          <MetricChipHead
            icon={<AvgIcon width={20} height={19} />}
            title="Generation"
            items={
              isTimeSlot
                ? [
                    {
                      label: "Max",
                      value: metricsLoading ? null : maxGen,
                      unit: "MW",
                    },
                    {
                      label: "Min",
                      value: metricsLoading ? null : minGen,
                      unit: "MW",
                    },
                  ]
                : [
                    {
                      label: "Peak",
                      value: metricsLoading ? null : peakGen,
                      unit: "MU",
                    },
                    {
                      label: "Average",
                      value: metricsLoading ? null : avgGen,
                      unit: "MU",
                    },
                  ]
            }
          />
          <MetricChipHead
            icon={<MinIcon width={20} height={19} />}
            title={isTimeSlot ? "Surplus/Deficit" : "Outage"}
            items={
              isTimeSlot
                ? [
                    {
                      label: "Max",
                      value: metricsLoading ? null : maxSurplus,
                      unit: "MW",
                    },
                    {
                      label: "Min",
                      value: metricsLoading ? null : minSurplus,
                      unit: "MW",
                    },
                  ]
                : [
                    {
                      label: "Peak",
                      value: metricsLoading ? null : peakOutage,
                      unit: "MW",
                    },
                    {
                      label: "Average",
                      value: metricsLoading ? null : avgOutage,
                      unit: "MW",
                    },
                  ]
            }
          />
          {isTimeSlot && (
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title={isTimeSlot ? "Frequency" : ""}
              items={[
                {
                  label: "Max",
                  value: metricsLoading ? null : maxFrency,
                  unit: "Hz",
                },
                {
                  label: "Avg",
                  value: metricsLoading ? null : avgFrency,
                  unit: "Hz",
                },
              ]}
            />
          )}
        </div>
        <div className="gas-chart-container">
          {dataLoading || slotDataLoading || filterLoading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div
                className="gas-heading gap-x-10 items-center relative z-[20]"
                style={{ minHeight: "52px" }}
              >
                <h2 className="chartHeading w-60 shrink-0">Overview</h2>
                <div className="flex justify-end flex-1 gap-x-4">
                  {/* Power Type SearchBox — shown when Generation By Source is active */}
                  {selectedMetrics.some(
                    (m) => m.suffix === "generation_by_source",
                  ) && (
                    <div className="flex gap-x-6 items-center">
                      <SearchBox
                        options={powerTypes.map((pt) => ({
                          value: pt,
                          label: pt,
                        }))}
                        isMany={true}
                        placeholder="Search Power Types"
                        selectedOptions={selectedPowerType.map((pt) => ({
                          value: pt,
                          label: pt,
                        }))}
                        onChange={(selected) =>
                          setSelectedPowerType(
                            (
                              selected as { value: string; label: string }[]
                            ).map((s) => s.value),
                          )
                        }
                      />
                      {/* <button
                    className="downloadButton"
                    onClick={() => setSelectedPowerType([...powerTypes])}
                  >
                    All
                  </button>
                  <button
                    className="downloadButton"
                    onClick={() => setSelectedPowerType([])}
                  >
                    Clear
                  </button> */}
                    </div>
                  )}

                  {/* Region SearchBox — shown when Geography = Region */}
                  {selectedGeography === "Region" && (
                    <div className="flex gap-x-6 items-center">
                      <SearchBox
                        options={REGIONS.map((r) => ({ value: r, label: r }))}
                        isMany={true}
                        placeholder="Search Regions"
                        selectedOptions={selectedRegions.map((r) => ({
                          value: r,
                          label: r,
                        }))}
                        onChange={(selected) => {
                          const newRegions = (
                            selected as { value: string; label: string }[]
                          ).map((s) => s.value);
                          const removedRegions = selectedRegions.filter(
                            (r) => !newRegions.includes(r),
                          );
                          if (removedRegions.length > 0) {
                            const removedStates = states.filter((s) =>
                              removedRegions.includes(
                                STATE_REGION_MAP[s] ?? "",
                              ),
                            );
                            setSelectedState((prev) =>
                              prev.filter(
                                (s) => !removedStates.includes(s.value),
                              ),
                            );
                          }
                          setSelectedRegions(newRegions);
                        }}
                      />
                      {/* <button
                    className="downloadButton"
                    onClick={() => setSelectedRegions([...REGIONS])}
                  >
                    All
                  </button>
                  <button
                    className="downloadButton"
                    onClick={() => {
                      setSelectedRegions([]);
                      setSelectedState([]);
                    }}
                  >
                    Clear
                  </button> */}
                    </div>
                  )}

                  {/* State SearchBox — shown when Geography = State */}
                  {selectedGeography === "State" && (
                    <div className="flex gap-x-6 items-center">
                      <SearchBox
                        options={(selectedInterval.key === "time_block"
                          ? slotStatesOptions
                          : states
                        ).map((item) => ({ value: item, label: item }))}
                        isMany={true}
                        placeholder="Search States"
                        selectedOptions={selectedState}
                        onChange={(e) => setSelectedState(e as any)}
                      />
                    </div>
                  )}
                  {isTimeSlot && (
                    <div className="flex gap-x-6 items-center">
                      <BlockRangeSelector
                        blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                        placeholder="Select Block"
                        selectedOptions={selectedBlock.map(
                          (item) => item.value,
                        )}
                        setSelectedOptions={setSelectedBlock}
                      />
                    </div>
                  )}
                </div>
              </div>

              {dataTab.label === "Chart" && !isHeatMap && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 5 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.key === "time_block"}
                  onlyTitle={true}
                  barCategoryGap={10}
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={
                    selectedInterval.key === "time_block"
                      ? "time_block"
                      : selectedInterval.values[0]
                  }
                  secondXDataKey={
                    selectedInterval.key === "time_block" ? "date" : undefined
                  }
                  customLegendOrder={legends.map((l: any) => l.name).sort()}
                  yAxisLabel="MW"
                  secondYAxisLabel={
                    hasRightAxis
                      ? legends.every(
                          (l: any) =>
                            l.yAxisId !== "right" ||
                            l.dataKey.includes("frequency_hz"),
                        )
                        ? "Hz"
                        : "MUs"
                      : ""
                  }
                  secondYAxisDomain={rightAxisDomain}
                />
              )}

              {dataTab.label === "Summary" && (
                <LegendSummaryTable
                  tableHeight="calc(100vh - 350px)"
                  {...tablePar}
                />
              )}
              {dataTab.label === "Table" && (
                <ChartTable tableHeight="calc(100vh - 350px)" {...tablePar} />
              )}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note="National Power Portal (Renewables), Grid Controller of India
                  Limited (Daily PSP Reports)"
              text="Info: DG = Diesel Generator | GT = Gas Turbine "
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
      )}
    </>
  );
}
